import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Collapse,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import { Project } from '../../models/Project';
import { User } from '../../models/User';
import { UserType } from '../../models/UserType';
import { UserRole } from '../../models/UserRole';
import { ProjectUser, isProjectClientUser, isProjectInternalUser } from '../../models/ProjectUser';
import {
  addUserToProject,
  removeUserFromProject,
  getAvailableEnrichedUsersForProject,
  getAssociatedUsersForProject,
  inviteUserToProject,
  ProjectInvitationRequest,
} from '../../api/projects-api';
import { LanguageCode } from '../../models/enums/LanguageCode';
import { useLanguage } from '../../i18n/LanguageContext';
import { useQueryClient } from '@tanstack/react-query';
import { zoullooColors } from '../../theme';
import { useTransferCsmLead } from '../../hooks/useAccountManagement';

interface ProjectContactsProps {
  project: Project;
  canEdit: boolean;
  currentUser: User;
}

// Language options for email invitation
const emailLanguageOptions = [
  { code: LanguageCode.Fr, label: 'Français' },
  { code: LanguageCode.En, label: 'English' },
];

export const ProjectContacts: React.FC<ProjectContactsProps> = ({
  project,
  canEdit,
  currentUser: _currentUser,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info'>('success');
  const queryClient = useQueryClient();
  const [showAddUsers, setShowAddUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ProjectUser[]>([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  const [associatedUsers, setAssociatedUsers] = useState<ProjectUser[]>([]);
  const [loadingAssociatedUsers, setLoadingAssociatedUsers] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [inviteFormData, setInviteFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    language: LanguageCode.Fr
  });
  const [invitationLoading, setInvitationLoading] = useState(false);
  const transferLeadMutation = useTransferCsmLead();

  // ----- CSM Lead helpers -----
  // The Lead is a flag carried by the InternalUserResponse for the project
  // (see ProjectsController.GetAssociatedInternalUsersForProject + UserMapper).
  // Roles come back from the backend in lowercase already (matches UserRole enum
  // values), but normalize defensively in case the JWT or seed data uses a
  // different case.
  const hasUserRole = (roles: string[] | undefined, role: UserRole): boolean =>
    !!roles?.some((r) => r.toLowerCase() === role);

  const isCsmUser = (user: ProjectUser) =>
    isProjectInternalUser(user) && hasUserRole(user.roles, UserRole.CSM);

  const isUserLead = (user: ProjectUser) =>
    isProjectInternalUser(user) && !!user.isLead;

  const isAdmin = hasUserRole(_currentUser.roles, UserRole.Admin);
  const currentUserIsLead = associatedUsers.some(
    (u) => u.id === _currentUser.id && isUserLead(u)
  );
  const projectHasLead = associatedUsers.some(isUserLead);
  const isCurrentUserAssociatedCsm = associatedUsers.some(
    (u) => u.id === _currentUser.id && isCsmUser(u)
  );

  /**
   * Whether the "Designate as Lead" star button should be shown on a given user row.
   *
   * Rules (matching backend authorization in ProjectsController.TransferCsmLead):
   *   - Admin or current Lead → button shown on every CSM row (except the Lead).
   *   - Associated CSM, with NO current Lead on the project → button shown only
   *     on THEIR OWN row, so they can self-qualify (PO requirement: any associated
   *     CSM can become Lead when there's none yet, but cannot promote someone else).
   *   - Otherwise hidden.
   */
  const canDesignateAsLead = (user: ProjectUser): boolean => {
    if (!isCsmUser(user) || isUserLead(user)) return false;
    if (isAdmin || currentUserIsLead) return true;
    if (!projectHasLead && isCurrentUserAssociatedCsm && user.id === _currentUser.id) return true;
    return false;
  };

  // Initialize invite form language with current user's language
  useEffect(() => {
    if (currentLanguage) {
      setInviteFormData(prev => ({ ...prev, language: currentLanguage }));
    }
  }, [currentLanguage]);
  
  // Ref for scrolling to contacts section
  const contactsSectionRef = useRef<HTMLDivElement>(null);

  // Load associated users when component mounts or project changes
  useEffect(() => {
    const loadAssociatedUsers = async () => {
      if (!project?.id) return;
      
      setLoadingAssociatedUsers(true);
      try {
        const users = await getAssociatedUsersForProject(project.id);

        setAssociatedUsers(users);
      } catch (error) {
        console.error('Error loading associated users:', error);
        setAlertMessage(t('projectContacts.errorLoadingUsers'));
        setAlertSeverity('error');
        setAlertOpen(true);
      } finally {
        setLoadingAssociatedUsers(false);
      }
    };

    loadAssociatedUsers();
  }, [project?.id, t]);

  const handleToggleAddUsers = async () => {
    const isOpening = !showAddUsers;
    
    if (isOpening && availableUsers.length === 0) {
      setLoadingAvailableUsers(true);
      try {
        const users = await getAvailableEnrichedUsersForProject(project.id, _currentUser.roles || []);

        setAvailableUsers(users);
        
        // Scroll after data is loaded
        setTimeout(() => {
          const element = contactsSectionRef.current;
          if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - 100; // 100px offset from top
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 200); // Wait a bit more for DOM update
        
      } catch (error) {
        console.error('Error loading available users:', error);
        setAlertMessage(t('projectContacts.errorLoadingUsers'));
        setAlertSeverity('error');
        setAlertOpen(true);
      } finally {
        setLoadingAvailableUsers(false);
      }
    } else if (isOpening) {
      // Data already loaded, just scroll
      setTimeout(() => {
        const element = contactsSectionRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100; // 100px offset from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    
    setShowAddUsers(!showAddUsers);
  };

  const handleAddUser = async (user: ProjectUser) => {
    setLoading(true);
    try {
      await addUserToProject(project.id, user.id);
      setAlertMessage(t('projectContacts.userAdded'));
      setAlertSeverity('success');
      setAlertOpen(true);
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      
      // Remove user from available list
      setAvailableUsers(prev => prev.filter(u => u.id !== user.id));
      
      // Reload associated users to get the new user with enriched info
      const updatedAssociatedUsers = await getAssociatedUsersForProject(project.id);
      setAssociatedUsers(updatedAssociatedUsers);
    } catch (error) {
      console.error('Error adding user to project:', error);
      setAlertMessage(t('projectContacts.errorAddingUser'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (userId === project.createdById) {
      setAlertMessage(t('projectContacts.cannotRemoveCreator'));
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setLoading(true);
    try {
      await removeUserFromProject(project.id, userId);
      setAlertMessage(t('projectContacts.userRemoved'));
      setAlertSeverity('success');
      setAlertOpen(true);
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      
      // Reload associated users to reflect the removal
      const updatedAssociatedUsers = await getAssociatedUsersForProject(project.id);
      setAssociatedUsers(updatedAssociatedUsers);
    } catch (error) {
      console.error('Error removing user from project:', error);
      setAlertMessage(t('projectContacts.errorRemovingUser'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDesignateAsLead = async (newLeadUserId: number) => {
    try {
      await transferLeadMutation.mutateAsync({
        projectId: project.id,
        data: { newLeadUserId },
      });
      setAlertMessage(t('projectContacts.leadTransferred', 'CSM Lead modifié avec succès'));
      setAlertSeverity('success');
      setAlertOpen(true);

      // Refetch associated users so the badge moves to the new Lead
      const updated = await getAssociatedUsersForProject(project.id);
      setAssociatedUsers(updated);
    } catch (error) {
      console.error('Error transferring CSM Lead:', error);
      setAlertMessage(
        t('projectContacts.leadTransferError', 'Erreur lors du changement de CSM Lead')
      );
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const toggleRowExpanded = (userId: number) => {
    setExpandedRows(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getCompanyName = (user: ProjectUser) => {
    if (isProjectClientUser(user) && user.client?.company) {
      // Use || instead of ?? to handle empty strings
      return user.client.company.tradingName || user.client.company.legalName || t('common.notProvided');
    }
    // For internal users, use enriched company information from backend
    if (!isProjectClientUser(user) && user.company) {
      return user.company.tradingName || user.company.legalName || t('common.notProvided');
    }
    return t('common.notProvided');
  };

  const getUserPhone = (user: ProjectUser) => {
    if (isProjectClientUser(user) && user.client?.phoneNumber) {
      return user.client.phoneNumber;
    }
    return t('common.notProvided');
  };

  const getUserJobTitle = (user: ProjectUser) => {
    if (isProjectClientUser(user) && user.client?.jobTitle) {
      return user.client.jobTitle;
    }
    // For internal users, use enriched job title from backend (CSM/TAM)
    if (!isProjectClientUser(user) && user.jobTitle) {
      return user.jobTitle;
    }
    return t('common.notProvided');
  };

  const canRemoveUser = (user: ProjectUser) => {
    if (user.id === project.createdById || !canEdit) {
      return false;
    }
    
    // Only Internal users (CSM) can remove contacts
    // Client users cannot remove any contacts
    if (_currentUser.type === UserType.Client) {
      return false;
    }
    
    // Only CSM can remove internal users
    if (isProjectInternalUser(user)) {
      return hasUserRole(_currentUser.roles, UserRole.CSM);
    }
    
    // Internal users with edit permission can remove client users
    return true;
  };

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setAlertMessage(t('projectContacts.emailCopied'));
      setAlertSeverity('success');
      setAlertOpen(true);
    } catch (error) {
      console.error('Failed to copy email:', error);
      setAlertMessage(t('projectContacts.emailCopyError'));
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const handleInviteFormChange = (field: keyof typeof inviteFormData) => (event: any) => {
    setInviteFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };


  const handleInviteUser = async () => {
    if (!inviteFormData.firstName || !inviteFormData.lastName || !inviteFormData.email) {
      setAlertMessage('Please fill in all fields');
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setInvitationLoading(true);
    try {
      const invitation: ProjectInvitationRequest = {
        firstName: inviteFormData.firstName,
        lastName: inviteFormData.lastName,
        email: inviteFormData.email,
        role: 4,  // CLIENT = 4 dans l'enum backend
        language: inviteFormData.language
      };

      const result = await inviteUserToProject(project.id, invitation);

      if (result.onboardingInProgress) {
        setAlertMessage(t('projectContacts.userOnboardingInProgress'));
        setAlertSeverity('info');
      } else {
        setAlertMessage(t('projectContacts.invitationSent'));
        setAlertSeverity('success');
      }
      setAlertOpen(true);

      // Reset form (keep the language for next invitation)
      setInviteFormData({
        firstName: '',
        lastName: '',
        email: '',
        language: inviteFormData.language
      });
      
      // Refresh associated users to show the new pending user
      const updatedAssociatedUsers = await getAssociatedUsersForProject(project.id);
      setAssociatedUsers(updatedAssociatedUsers);
      
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    } catch (error) {
      console.error('Error inviting user:', error);
      setAlertMessage(t('projectContacts.errorSendingInvitation'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setInvitationLoading(false);
    }
  };

  const getUserStatusChip = (user: ProjectUser) => {
    // Use the status property from the API
    const status = (user as any).status;

    const isPending = status === 'Pending' || status === 0; // UserStatus.Pending = 0

    // Client user: active but onboarding not completed
    const isOnboardingInProgress = !isPending && isProjectClientUser(user) && user.client?.onboardingCompleted === false;

    let label: string;
    let color: 'warning' | 'success' | 'info';

    if (isPending) {
      label = t('projectContacts.statusPending');
      color = 'warning';
    } else if (isOnboardingInProgress) {
      label = t('projectContacts.statusOnboardingInProgress');
      color = 'info';
    } else {
      label = t('projectContacts.statusActive');
      color = 'success';
    }

    return (
      <Chip
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 2 }} ref={contactsSectionRef}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" color={zoullooColors.secondary.dark}>
          {t('projectContacts.title')}
        </Typography>
        {canEdit && (
          <Button
            startIcon={<PersonAddIcon />}
            variant="outlined"
            onClick={handleToggleAddUsers}
            disabled={loading}
            sx={{
              borderColor: zoullooColors.secondary.main,
              color: zoullooColors.secondary.main,
              '&:hover': {
                borderColor: zoullooColors.secondary.dark,
                backgroundColor: zoullooColors.secondary.alpha,
              },
            }}
          >
            {t('projectContacts.addContact')}
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('projectContacts.nameAndSurname')}</TableCell>
              <TableCell>{t('projectContacts.company')}</TableCell>
              <TableCell>{t('projectContacts.function')}</TableCell>
              <TableCell>{t('projectContacts.userStatus')}</TableCell>
              <TableCell align="center">{t('projectContacts.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingAssociatedUsers ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (associatedUsers?.length ?? 0) > 0 ? (
              <>
                {associatedUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <TableRow>
                      <TableCell>
                        <Typography sx={{ color: user.id === project.createdById ? zoullooColors.secondary.main : 'inherit' }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        {user.id === project.createdById && (
                          <Chip
                            label={t('projectContacts.projectCreator')}
                            size="small"
                            sx={{
                              ml: 1,
                              backgroundColor: zoullooColors.secondary.main,
                              color: zoullooColors.text.white
                            }}
                          />
                        )}
                        {isUserLead(user) && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            label={t('projectContacts.csmLead', 'CSM Lead')}
                            size="small"
                            color="warning"
                            sx={{ ml: 1, fontWeight: 600 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{getCompanyName(user)}</TableCell>
                      <TableCell>{getUserJobTitle(user)}</TableCell>
                      <TableCell>{getUserStatusChip(user)}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1} alignItems="center">
                          {/* "Désigner Lead" button — visibility per row, see canDesignateAsLead.
                              Admin / current Lead can designate any CSM; an associated CSM
                              can self-qualify when no Lead exists yet. */}
                          {canDesignateAsLead(user) && (
                            <IconButton
                              size="small"
                              onClick={() => handleDesignateAsLead(user.id)}
                              disabled={transferLeadMutation.isPending}
                              title={
                                user.id === _currentUser.id
                                  ? t(
                                      'projectContacts.designateMyselfAsLead',
                                      'Me désigner comme CSM Lead'
                                    )
                                  : t(
                                      'projectContacts.designateAsLead',
                                      'Désigner comme CSM Lead'
                                    )
                              }
                              sx={{ color: zoullooColors.secondary.main }}
                            >
                              <StarBorderIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => toggleRowExpanded(user.id)}
                            endIcon={expandedRows.includes(user.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          >
                            {expandedRows.includes(user.id) ? t('projectContacts.hide') : t('projectContacts.show')}
                          </Button>
                          {canRemoveUser(user) && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveUser(user.id)}
                              disabled={loading}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expandedRows.includes(user.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>{t('projectContacts.email')}:</strong>{' '}
                              <Typography
                                component="span"
                                variant="body2"
                                onClick={() => handleCopyEmail(user.email)}
                                sx={{
                                  color: zoullooColors.text.primary,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  '&:hover': {
                                    color: zoullooColors.grey[700],
                                  },
                                }}
                                title={t('projectContacts.clickToCopyEmail')}
                              >
                                {user.email}
                              </Typography>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>{t('projectContacts.phone')}:</strong> {getUserPhone(user)}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Alert severity="info" sx={{ display: 'inline-flex' }}>
                    {t('projectContacts.noContacts')}
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Available Users Section - Shows below the table when Add Contact is clicked */}
      <Collapse in={showAddUsers} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 3, p: 2, backgroundColor: zoullooColors.background.light, borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('projectContacts.availableUsers')}
          </Typography>
          {loadingAvailableUsers ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (availableUsers?.length ?? 0) > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('projectContacts.nameAndSurname')}</TableCell>
                    <TableCell>{t('projectContacts.company')}</TableCell>
                    <TableCell>{t('projectContacts.function')}</TableCell>
                    <TableCell>{t('projectContacts.email')}</TableCell>
                    <TableCell align="center">{t('projectContacts.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{getCompanyName(user)}</TableCell>
                      <TableCell>{getUserJobTitle(user)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={() => handleCopyEmail(user.email)}
                          sx={{
                            color: zoullooColors.text.primary,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: zoullooColors.grey[700],
                            },
                          }}
                          title={t('projectContacts.clickToCopyEmail')}
                        >
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddUser(user)}
                          disabled={loading}
                          sx={{
                            backgroundColor: zoullooColors.secondary.main,
                            color: zoullooColors.secondary.contrastText,
                            '&:hover': {
                              backgroundColor: zoullooColors.secondary.dark,
                            },
                          }}
                        >
                          {t('common.add')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">{t('projectContacts.noAvailableUsers')}</Alert>
          )}

          {/* Invitation Form - Always show when Add Contact section is open */}
          {canEdit && (
            <Box sx={{ mt: 3, p: 3, backgroundColor: zoullooColors.background.paper, borderRadius: 1, border: `1px solid ${zoullooColors.border.light}` }}>
              <Typography variant="h6" gutterBottom color={zoullooColors.secondary.dark}>
                {t('projectContacts.inviteNewContactToProject')}
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    label={t('projectContacts.firstName')}
                    value={inviteFormData.firstName}
                    onChange={handleInviteFormChange('firstName')}
                    disabled={invitationLoading}
                    size="small"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    label={t('projectContacts.lastName')}
                    value={inviteFormData.lastName}
                    onChange={handleInviteFormChange('lastName')}
                    disabled={invitationLoading}
                    size="small"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label={t('projectContacts.email')}
                    value={inviteFormData.email}
                    onChange={handleInviteFormChange('email')}
                    disabled={invitationLoading}
                    size="small"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    value={emailLanguageOptions.find(opt => opt.code === inviteFormData.language) || emailLanguageOptions[0]}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        setInviteFormData(prev => ({ ...prev, language: newValue.code }));
                      }
                    }}
                    options={emailLanguageOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    disableClearable
                    disabled={invitationLoading}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('projectContacts.emailLanguage')}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <Button
                    variant="contained"
                    onClick={handleInviteUser}
                    disabled={invitationLoading}
                    sx={{
                      backgroundColor: zoullooColors.secondary.main,
                      color: zoullooColors.secondary.contrastText,
                      '&:hover': {
                        backgroundColor: zoullooColors.secondary.dark,
                      },
                      minWidth: 120,
                    }}
                  >
                    {invitationLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t('projectContacts.inviteButton')
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Collapse>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setAlertOpen(false)} 
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};